import { useState } from 'react';
import axios from 'axios';
import { submitAttendance } from '../services/googleScriptServiceCORS';
import { getSuccessMessage } from '../utils/greetings';
import { perfStart, perfEnd } from '../utils/performance';
import { getDeviceType } from '../utils/deviceDetection';
import { writeLocalTodayAttendance } from '../utils/todayAttendanceLocal';

const generateRequestId = (type, employeeId) => {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${Date.now()}_${employeeId || 'emp'}_${type}_${rand}`;
};

const CLIENT_CHANNEL_OVERRIDE = (process.env.REACT_APP_ATTENDANCE_CLIENT_CHANNEL || '').trim().toLowerCase();
const resolveAttendanceClientChannel = () => {
  if (CLIENT_CHANNEL_OVERRIDE) return CLIENT_CHANNEL_OVERRIDE;
  return getDeviceType() === 'mobile' ? 'mobile_web_app' : 'web_portal';
};

const normalizeText = (value) => String(value || '').trim();

const buildSalesBusinessReportText = (salesReportData) => {
  if (!salesReportData) return '';

  const sections = [
    { label: 'Làm việc tại văn phòng', value: salesReportData.office_work_report },
    { label: 'Công tác bên ngoài văn phòng', value: salesReportData.outside_office_report },
    { label: 'Công tác tỉnh', value: salesReportData.province_trip_report },
    { label: 'Đào tạo / hội thảo', value: salesReportData.training_event_report },
  ];

  const content = sections
    .map((section) => ({ label: section.label, value: normalizeText(section.value) }))
    .filter((section) => section.value)
    .map((section) => `[${section.label}] ${section.value}`);

  return content.join(' | ');
};

const hasAnySalesReportContent = (salesReportData) => {
  if (!salesReportData) return false;
  return [
    salesReportData.office_work_report,
    salesReportData.outside_office_report,
    salesReportData.province_trip_report,
    salesReportData.training_event_report,
  ].some((value) => normalizeText(value) !== '');
};

export function useAttendanceSubmit({
  employee,
  apiBaseUrl,
  location,
  image,
  explanation,
  businessReport = '',
  requireBusinessReport = false,
  isSalesEmployee = false,
  salesReportData = null,
  requiresExplanation,
  validationWarnings,
  setValidationWarnings,
  setRequiresExplanation,
  setExplanation,
  setBusinessReport = null,
  resetSalesReportForm = null,
  setImage,
  setImagePreview,
  setTodayAttendance,
  loadTodayAttendance,
  checkValidation,
  setInlineStatus,
  showToast,
  enqueueOfflineIfNeeded,
  flushOfflineQueue = null,
  strictWebAttendanceDisabled = false,
}) {
  const [loading, setLoading] = useState(false);

  const shouldUseDetailedCheckoutReport = (type) =>
    type === 'checkout' && (isSalesEmployee || requireBusinessReport) && !!salesReportData;
  const isBusinessReportRequiredForType = (type) => type === 'checkout' && requireBusinessReport;

  const resolveEffectiveBusinessReport = (type) => {
    const directBusinessReport = normalizeText(businessReport);
    if (directBusinessReport) return directBusinessReport;

    if (shouldUseDetailedCheckoutReport(type)) {
      return buildSalesBusinessReportText(salesReportData);
    }

    return '';
  };

  const buildPayload = (type, requestId) => {
    const effectiveBusinessReport = resolveEffectiveBusinessReport(type);

    const basePayload = {
      employee_id: employee.id,
      employee_code: employee.code,
      employee_name: employee.name,
      request_id: requestId || '',
      type,
      latitude: location?.latitude,
      longitude: location?.longitude,
      accuracy: location?.accuracy || null,
      address: location?.address || '',
      explanation: explanation.trim(),
      business_report: effectiveBusinessReport,
    };

    if (shouldUseDetailedCheckoutReport(type)) {
      basePayload.work_status_final = salesReportData.work_status_final || '';
      basePayload.office_work_report = salesReportData.office_work_report || '';
      basePayload.outside_office_report = salesReportData.outside_office_report || '';
      basePayload.province_trip_report = salesReportData.province_trip_report || '';
      basePayload.training_event_report = salesReportData.training_event_report || '';
    }

    return basePayload;
  };

  const validateBeforeSubmit = (type) => {
    if (!location) {
      const msg = 'Vui lòng lấy vị trí trước khi chấm công.';
      setInlineStatus({ type: 'warning', message: msg });
      showToast(msg, 'warning');
      return false;
    }

    if (!image) {
      const msg = 'Vui lòng chụp/chọn ảnh trước khi chấm công.';
      setInlineStatus({ type: 'warning', message: msg });
      showToast(msg, 'warning');
      return false;
    }

    const { needExplanation } = checkValidation(type);
    if ((needExplanation || requiresExplanation) && !explanation.trim()) {
      const msg = validationWarnings.length
        ? `Vui lòng nhập giải trình vì:\n${validationWarnings.join('\n')}`
        : 'Vui lòng nhập giải trình để tiếp tục.';
      setInlineStatus({ type: 'warning', message: msg });
      showToast('Vui lòng nhập giải trình.', 'warning');
      return false;
    }

    if (shouldUseDetailedCheckoutReport(type) && !hasAnySalesReportContent(salesReportData)) {
      const msg =
        'Vui lòng nhập ít nhất 1 nội dung ở mục 2/3/4/5 của Báo cáo kinh doanh.';
      setInlineStatus({ type: 'warning', message: msg });
      showToast(msg, 'warning');
      return false;
    }

    const effectiveBusinessReport = resolveEffectiveBusinessReport(type);
    if (isBusinessReportRequiredForType(type) && !effectiveBusinessReport) {
      const msg = 'Vui lòng nhập Báo cáo Kinh doanh trước khi chấm công.';
      setInlineStatus({ type: 'warning', message: msg });
      showToast(msg, 'warning');
      return false;
    }

    return true;
  };

  const resetAfterSuccess = () => {
    setImage(null);
    setImagePreview(null);
    setExplanation('');
    if (typeof setBusinessReport === 'function') {
      setBusinessReport('');
    }
    if (typeof resetSalesReportForm === 'function') {
      resetSalesReportForm();
    }
    setRequiresExplanation(false);
    setValidationWarnings([]);
  };

  const submitViaFastApi = async (type, requestId) => {
    const clientChannel = resolveAttendanceClientChannel();
    const clientOrigin = typeof window !== 'undefined' && window.location ? (window.location.origin || '') : '';
    const formData = new FormData();

    formData.append('employee_id', employee.id);
    formData.append('latitude', location.latitude);
    formData.append('longitude', location.longitude);
    formData.append('address', location.address || '');
    formData.append('request_id', requestId || '');
    formData.append('client_channel', clientChannel);
    formData.append('client_app', clientChannel);
    if (clientOrigin) {
      formData.append('client_origin', clientOrigin);
    }
    formData.append('image', image);

    const effectiveBusinessReport = resolveEffectiveBusinessReport(type);
    if (effectiveBusinessReport) {
      formData.append('business_report', effectiveBusinessReport);
    }

    if (shouldUseDetailedCheckoutReport(type)) {
      formData.append('work_status_final', salesReportData.work_status_final || '');
      formData.append('office_work_report', salesReportData.office_work_report || '');
      formData.append('outside_office_report', salesReportData.outside_office_report || '');
      formData.append('province_trip_report', salesReportData.province_trip_report || '');
      formData.append('training_event_report', salesReportData.training_event_report || '');
    }

    await axios.post(`${apiBaseUrl}/api/attendance/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const submitGoogleScript = async (type, requestId) => {
    const result = await submitAttendance({ ...buildPayload(type, requestId), image });

    if (result.ok) {
      if (type === 'checkin') {
        const checkinTime = result.timestamp || new Date().toISOString();
        const snapshot = {
          checkin_time: checkinTime,
          checkout_time: null,
          work_hours: 0,
        };
        setTodayAttendance(snapshot);
        writeLocalTodayAttendance(employee, snapshot);
      } else {
        setTodayAttendance((prev) => {
          if (!prev?.checkin_time) return prev;
          const checkoutTime = result.timestamp || new Date().toISOString();
          const workHours = (new Date(checkoutTime) - new Date(prev.checkin_time)) / (1000 * 60 * 60);
          const next = {
            ...prev,
            checkout_time: checkoutTime,
            work_hours: workHours > 0 ? workHours : 0,
          };
          writeLocalTodayAttendance(employee, next);
          return next;
        });
      }
      return;
    }

    if (result.code === 'REQUIRES_EXPLANATION' || result.requiresExplanation) {
      setRequiresExplanation(true);
      setValidationWarnings([result.error, result.note].filter(Boolean));
      throw new Error(result.error || 'Chấm công cần giải trình.');
    }

    throw new Error(result.error || 'Có lỗi xảy ra');
  };

  const runSubmit = async (type) => {
    if (strictWebAttendanceDisabled) {
      const msg = 'Chế độ strict đang bật: Web thường không được phép chấm công.';
      setInlineStatus({ type: 'warning', message: msg });
      showToast(msg, 'warning');
      return;
    }

    const requestId = generateRequestId(type, employee?.id);
    if (!validateBeforeSubmit(type)) return;

    const metric = type === 'checkin' ? 'attendance_checkin_submit' : 'attendance_checkout_submit';
    setLoading(true);
    perfStart(metric);

    try {
      const useGoogleScript = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
      if (useGoogleScript) {
        await submitGoogleScript(type, requestId);
      } else {
        await submitViaFastApi(type, requestId);
        await loadTodayAttendance();
      }

      const msg = getSuccessMessage(type);
      setInlineStatus({ type: 'success', message: msg });
      showToast(msg, 'success');
      resetAfterSuccess();
      if (typeof flushOfflineQueue === 'function') {
        flushOfflineQueue({ silent: true }).catch(() => {});
      }
    } catch (error) {
      const queued = await enqueueOfflineIfNeeded({ payload: buildPayload(type, requestId), image, error });
      if (!queued) {
        const errMsg = error.message || error.response?.data?.detail || 'Có lỗi xảy ra khi chấm công';
        setInlineStatus({ type: 'error', message: errMsg });
        showToast(errMsg, 'error');
      }
    } finally {
      perfEnd(metric);
      setLoading(false);
    }
  };

  return {
    loading,
    handleCheckin: () => runSubmit('checkin'),
    handleCheckout: () => runSubmit('checkout'),
  };
}


